import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Button, IconButton, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { useState } from 'react';
import type { Tender } from '../../types/tender';
import { calculateDaysUntil, displayDaysUntilDeadline, getDeadlineDate } from './utils';

interface TenderInfoCardProps {
  tender: Tender;
  onUpdate?: (field: keyof Tender, value: Tender[keyof Tender]) => Promise<void>;
}

const TenderInfoCard: React.FC<TenderInfoCardProps> = ({ tender, onUpdate }) => {
  const theme = useTheme();
  const [isEditingDeadline, setIsEditingDeadline] = useState(false);
  const [deadlineValue, setDeadlineValue] = useState<string>('');

  const handleEditClick = () => {
    const deadline = getDeadlineDate(tender);
    setDeadlineValue(deadline ? deadline.toISOString().split('T')[0] || '' : '');
    setIsEditingDeadline(true);
  };

  const handleSaveClick = async () => {
    if (onUpdate) {
      await onUpdate('deadlineDate', deadlineValue); // Updating deadlineDate string/date
    }
    setIsEditingDeadline(false);
  };

  const handleCancelClick = () => {
    setIsEditingDeadline(false);
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        wordBreak: 'break-word',
      }}
    >
      <Typography variant="subtitle1" sx={{ fontSize: '1rem' }}>
        <strong>Issued by:</strong> {tender.tenderIssuer || 'IssuerMissing'}
      </Typography>
      {(() => {
        const deadline = getDeadlineDate(tender);
        const daysUntil = deadline ? calculateDaysUntil(deadline) : tender.daysUntilDeadline;

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" component="div" sx={{ mr: 1, fontSize: '1rem' }}>
              <strong>Closing Date:</strong>
            </Typography>
            {isEditingDeadline ? (
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  type="date"
                  size="small"
                  value={deadlineValue}
                  onChange={(e) => setDeadlineValue(e.target.value)}
                  sx={{ mr: 1 }}
                />
                <IconButton onClick={handleSaveClick} size="small" color="primary">
                  <SaveIcon fontSize="small" />
                </IconButton>
                <IconButton onClick={handleCancelClick} size="small" color="error">
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <>
                <Typography variant="subtitle1" component="span" sx={{ fontSize: '1rem' }}>
                  {deadline
                    ? deadline.toLocaleDateString()
                    : tender.deadlineDate
                      ? new Date(tender.deadlineDate).toLocaleDateString()
                      : 'N/A'}{' '}
                  ({displayDaysUntilDeadline(daysUntil)})
                </Typography>
                {onUpdate && (
                  <Button
                    variant="text"
                    onClick={handleEditClick}
                    size="small"
                    startIcon={<EditIcon fontSize="small" />}
                    sx={{ ml: 1, textTransform: 'none' }}
                  >
                    Edit
                  </Button>
                )}
              </>
            )}
          </Box>
        );
      })()}
      <Typography variant="subtitle1" sx={{ fontSize: '1rem' }}>
        <strong>Project Duration:</strong> {tender.projectDuration || 'N/A'}
      </Typography>
      <Typography variant="subtitle1" sx={{ fontSize: '1rem' }}>
        <strong>Expected Start Date:</strong> {tender.expectedStartDate || 'N/A'}
      </Typography>
      <Typography variant="subtitle1" sx={{ fontSize: '1rem' }}>
        <strong>Category:</strong> {tender.category || 'N/A'}
      </Typography>
      <Typography variant="subtitle1" sx={{ fontSize: '1rem' }}>
        <strong>Contact Person:</strong> {tender.contactPersonName || 'Not Disclosed'}
      </Typography>
      {tender.contactPersonEmail && tender.contactPersonEmail !== 'Not Disclosed' ? (
        <Typography variant="subtitle1" sx={{ fontSize: '1rem' }}>
          <strong>Contact Email:</strong>{' '}
          <a href={`mailto:${tender.contactPersonEmail}`} style={{ color: 'inherit' }}>
            {tender.contactPersonEmail}
          </a>
        </Typography>
      ) : (
        <Typography variant="subtitle1" sx={{ fontSize: '1rem' }}>
          <strong>Contact Email:</strong> Not Disclosed
        </Typography>
      )}
      <Typography variant="subtitle1" sx={{ fontSize: '1rem' }}>
        <strong>Tender ID:</strong> {(() => {
          let displayId =
            tender.tenderId && tender.tenderId !== 'Not found'
              ? tender.tenderId
              : tender.opportunityID && tender.opportunityID !== 'Not found'
                ? tender.opportunityID
                : tender.id || 'N/A';

          // Strip UID prefix if present (assumes format uid_realId)
          if (displayId?.includes('_')) {
            const parts = displayId.split('_');
            if (parts.length > 1) {
              displayId = parts.slice(1).join('_');
            }
          }

          if (displayId && displayId.length > 20) {
            displayId = `${displayId.substring(0, 20)}...`;
          }

          return tender.tenderSourceURL ? (
            <a
              href={tender.tenderSourceURL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontWeight: 600, color: theme.palette.bidlogiq.brick }}
            >
              {displayId}
            </a>
          ) : (
            displayId
          );
        })()}
      </Typography>
    </Box>
  );
};

export default TenderInfoCard;
